/**
 * CSC declined the position, but suggested another nearby. The captain answers.
 *
 * The decision this screen supports is not "yes or no" in the abstract -- it is
 * "can I lie there". So it leads with the chart showing both pins and the line
 * between them, then the offset in words, then CSC's own note. Two sets of
 * coordinates in a list would be technically complete and useless: no captain
 * can tell from `60°18.15'N 005°18.06'E` whether that is inside the shelter or
 * out in the tide.
 *
 * Accepting is not an edit. It files a **new** application at the proposed
 * position, carrying this window forward, and closes this one -- because
 * `ANK-…` is already quoted in the email CSC answered, and a reference that
 * silently starts pointing at a different position would make ABG's record
 * disagree with what CSC actually saw. The copy says so, in one line, because a
 * captain who thinks their application was edited will not understand why the
 * reference on their approval is different.
 *
 * Deliberately no third button. "Ask ABG about this" would need a message
 * channel, and there is none in this product by design -- the captain has ABG's
 * telephone number, which is a better tool for "why?" than any form.
 */

import { useState } from 'react'
import { Notice, formatPosition } from '@abg/ui'
import { PositionPreview } from './PositionPreview'
import type { Application } from '../lib/types'

const COMPASS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
]

/**
 * The offset in words. The server sends the same phrase in the push body; this
 * renders from the numbers so the two cannot disagree about rounding, and both
 * come from one SQL computation rather than from either client's own
 * trigonometry.
 */
function offset(app: Application): string | null {
  if (app.proposed_distance_m === null || app.proposed_bearing_deg === null) return null
  const metres = Math.round(app.proposed_distance_m)
  if (metres < 1) return null
  const point = COMPASS[Math.round((((app.proposed_bearing_deg % 360) + 360) % 360) / 22.5) % 16]
  return `${metres} m ${point}`
}

export function AlternativeOffer({
  application,
  busy,
  onAccept,
  onDecline,
}: {
  application: Application
  busy: string | null
  onAccept: () => void
  onDecline: (reason: string) => void
}) {
  const [declining, setDeclining] = useState(false)
  const [reason, setReason] = useState('')

  if (application.proposed_lat === null || application.proposed_lon === null) return null
  const moved = offset(application)

  return (
    <section className="card stack">
      <Notice tone="warn">
        <strong>The position you asked for was not granted.</strong>
        <p style={{ margin: 'var(--space-2) 0 0' }}>
          A different position nearby was suggested instead
          {moved ? <> — <strong>{moved}</strong> of where you asked</> : null}. Nothing happens
          until you answer.
        </p>
      </Notice>

      <PositionPreview
        lat={application.lat}
        lon={application.lon}
        compare={{ lat: application.proposed_lat, lon: application.proposed_lon }}
        height={240}
      />
      <p className="caption">
        Filled pin: where you asked. Hollow amber pin: what was suggested.
      </p>

      <dl className="kv">
        <dt>You asked for</dt>
        <dd className="mono">{formatPosition(application.lat, application.lon)}</dd>
        <dt>Suggested</dt>
        <dd className="mono">
          {formatPosition(application.proposed_lat, application.proposed_lon)}
        </dd>
        {application.proposed_note && (
          <>
            <dt>Reason given</dt>
            <dd>{application.proposed_note}</dd>
          </>
        )}
      </dl>

      <p className="caption">
        Your window is unchanged. Accepting files a new application at the suggested position and
        closes this one, so you will get a new reference — we have already quoted this one to the
        authority.
      </p>

      {!declining ? (
        <div className="action-stack">
          <button
            type="button"
            className="btn btn-lg btn-block"
            disabled={busy !== null}
            onClick={onAccept}
          >
            {busy === 'accept' ? 'Filing…' : 'Accept the suggested position'}
          </button>
          <button
            type="button"
            className="btn btn-quiet btn-block"
            disabled={busy !== null}
            onClick={() => setDeclining(true)}
          >
            Neither — withdraw
          </button>
        </div>
      ) : (
        <div className="stack-sm">
          <label htmlFor="decline-reason">
            Why not? <span className="caption">Optional, and we will read it.</span>
          </label>
          <textarea
            id="decline-reason"
            rows={3}
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Too exposed for our draught…"
          />
          <div className="action-stack">
            <button
              type="button"
              className="btn btn-block btn-danger"
              disabled={busy !== null}
              onClick={() => onDecline(reason.trim())}
            >
              {busy === 'decline' ? 'Withdrawing…' : 'Withdraw the application'}
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-block"
              disabled={busy !== null}
              onClick={() => setDeclining(false)}
            >
              Back
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
